import clx from 'classnames';
import { type ReactNode } from 'react';
import { useWatch } from 'react-hook-form';
import { IsRadioOrCheckboxGroupElementSymbol } from '../../item/symbols/isRadioOrCheckboxGroupElement.tsx';
import { useFormItemEnabled } from '../../item/useFormItemEnabled.ts';
import {
  type IRadioGroupInputProps,
  RadioGroupInput,
} from '../RadioGroupInput.tsx';
import { RadioOptionInput } from '../RadioOptionInput.tsx';
import {
  CardGroupLayout,
  type ICardGroupLayoutProps,
} from './CardGroupLayout.tsx';
import {
  CardOptionLayout,
  type ICardOptionLayoutProps,
} from './CardOptionLayout.tsx';

export interface IRadioCardOption<T = string | number> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface IRadioCardGroupInputProps
  extends Omit<IRadioGroupInputProps, 'children' | 'options' | 'vertical'>,
    Pick<ICardGroupLayoutProps, 'vertical' | 'gridColumns' | 'noLabelOffset'>,
    Partial<Pick<ICardOptionLayoutProps, 'indicatorPosition'>> {
  options: IRadioCardOption[];
}

/**
 * Radio group con opzioni renderizzate come card.
 *
 * Ogni opzione è una card con bordo, contenuto personalizzabile e pallino radio di Ant Design.
 * Eredita accessibilità completa (role, aria-*, tastiera) da RadioGroupInput.
 * Sotto HORIZONTAL_BREAKPOINT (600px) il layout orizzontale passa automaticamente a verticale.
 *
 * Props composte da:
 * - IRadioGroupInputProps (name, readOnly, onChange, transformValue)
 * - ICardGroupLayoutProps (vertical, gridColumns)
 * - ICardOptionLayoutProps (indicatorPosition)
 *
 * @see CardGroupLayout - gestisce il layout flex (vertical, responsive)
 * @see CardOptionLayout - gestisce lo stile della singola card (bordo, stati, indicatore)
 */
export function RadioCardGroupInput(props: IRadioCardGroupInputProps) {
  const {
    options,
    vertical,
    gridColumns,
    indicatorPosition = 'end',
    noLabelOffset,
    className,
    name,
    transformValue,
    readOnly,
    ...restProps
  } = props;

  const { visuallyDisabled } = useFormItemEnabled();

  const value = useWatch({ name });
  const transformedValue = transformValue?.(value) ?? value;

  return (
    <CardGroupLayout
      vertical={vertical}
      gridColumns={gridColumns}
      noLabelOffset={noLabelOffset}
    >
      {effectiveVertical => (
        <RadioGroupInput
          {...restProps}
          name={name}
          readOnly={readOnly}
          transformValue={transformValue}
          vertical={effectiveVertical}
          className={clx(className, 'card-group')}
        >
          {options.map(option => (
            <CardOptionLayout
              key={option.value}
              indicatorPosition={indicatorPosition}
              selected={transformedValue === option.value}
              disabled={!!option.disabled || !!visuallyDisabled}
              readOnly={!!readOnly}
            >
              <RadioOptionInput
                name={name}
                optionValue={option.value}
                disabled={option.disabled}
                readOnly={readOnly}
                transformValue={transformValue}
              >
                <div className="card-option-content">{option.label}</div>
              </RadioOptionInput>
            </CardOptionLayout>
          ))}
        </RadioGroupInput>
      )}
    </CardGroupLayout>
  );
}

RadioCardGroupInput[IsRadioOrCheckboxGroupElementSymbol] = true;
